/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Wednesday, May 4th 2022, 11:39:50 am
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import { WritableModel } from "lib/schemas/abstract";
import { schemaBuilder } from "lib/validation";
import { isPhoneNumber } from "lib/validation";
import { User, USER_TYPES } from "./User";
import { Organization } from "lib/schemas/organizations/Organization";
import { Department } from "lib/schemas/organizations/Department";

type WorkerTypes = Exclude<keyof typeof USER_TYPES, "CLIENT">;

export enum WorkerStatus {
  ACTIVE = "active",
  INACTIVE = "inactive",
}

export interface WorkProfile extends WritableModel<true> {
  job_title?: string;
  work_identification_number?: string;
  work_email?: string;
  work_phone?: string;
  readonly worker_type?: WorkerTypes;
  readonly status: keyof typeof WorkerStatus;
  user: User["id"];
  organization: Organization["id"];
  department: Department["id"];
  readonly creation_date?: Date;
}

export type WorkProfilePatch = Required<Pick<WorkProfile, "id">> & Partial<WorkProfile>;

// For form validation, omitting values that should be updated by server operations i.e. Worker type and status
// should only be updated by the server on operations i.e. worker type set by invite, administrator set by
// adding to organization admins.

type WriteableWorkProfile = Omit<
  WorkProfile,
  "id" | "creation_date" | "worker_type" | "status" | "user" | "organization" | "department"
>;

export const WorkProfileValidationSchema: schemaBuilder.SchemaOf<WriteableWorkProfile> = schemaBuilder.object().shape({
  job_title: schemaBuilder.string().optional().nullable(),
  work_identification_number: schemaBuilder.string().optional().nullable().default(null),
  work_email: schemaBuilder.string().email().optional().nullable().default(null),
  work_phone: schemaBuilder
    .string()
    .optional()
    .nullable()
    .default(null)
    .test("valid-phone-number", "Please provide a valid phone number", (v) => isPhoneNumber(v)),
});
